import React, { useState, useEffect } from 'react'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { db } from '../firebase'
import { ShopFormLayout } from '../layouts/ShopFormLayout'
import { uploadToCloudinary } from '../utils/uploadToCloudinary'
import { useAuthWatcher } from '../hooks/useAuthWatcher'
import { Shop } from '../type/common'

export const EditShopPage = () => {
  const { shopId } = useParams<{ shopId: string }>()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuthWatcher()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    tags: '',
    imageUrl: '',
  })
  const [isLoading, setIsLoading] = useState(true)

  const fetchShop = async () => {
    if (!shopId || !user?.uid) return

    try {
      const docRef = doc(db, 'shops', shopId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data() as Shop

        // 権限チェック
        if (data.userId !== user.uid) {
          navigate('/')
          return
        }

        // フォームデータに既存データをセット
        setFormData({
          name: data.name || '',
          description: data.description || '',
          address: data.address || '',
          tags: Array.isArray(data.tags) ? data.tags.join('、 ') : data.tags || '',
          imageUrl: data.imageUrl || '',
        })
      } else {
        navigate('/')
      }
    } catch (error) {
      navigate('/')
    } finally {
      setIsLoading(false)
    }
  }

  // 既存データの取得
  useEffect(() => {
    if (!authLoading && user) {
      fetchShop()
    }
  }, [shopId, user, authLoading, navigate])

  // フォームデータ変更時の汎用ハンドラ
  const handleFormDataChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // 更新処理
  const handleSubmit = async (file: File | null) => {
    if (!shopId) return

    let uploadImageUrl = formData.imageUrl

    // 新しい画像がアップロードされた場合
    if (file) {
      uploadImageUrl = await uploadToCloudinary(file)
    } else if (file === null && !formData.imageUrl) {
      // 画像が削除された場合
      uploadImageUrl = ''
    }

    const shopData = {
      ...formData,
      tags: formData.tags.trim()
        ? formData.tags
            .split(/[、,]/)
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
        : [],
      imageUrl: uploadImageUrl,
      updatedAt: new Date(),
    }

    await updateDoc(doc(db, 'shops', shopId), shopData)
  }

  if (authLoading || isLoading) {
    return (
      <Box height="50vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <ShopFormLayout
      title="お店を編集"
      formData={formData}
      onFormDataChange={handleFormDataChange}
      onSubmit={handleSubmit}
      existingImageUrl={formData.imageUrl}
      isEditMode
    />
  )
}
